import { zodResolver } from '@hookform/resolvers/zod'
import { SocialEmailAccountExistsError } from '@longy/common'
import {
    authPillButtonClass,
    Button,
    Checkbox,
    Form,
    FormPasswordField,
    FormTextField,
    Icons,
    Label,
} from '@longy/web-components'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { AuthLayout } from '../components/auth/AuthLayout'
import { SocialConsentStep } from '../components/auth/SocialConsentStep'
import { SocialLoginButtons } from '../components/auth/SocialLoginButtons'
import { api, getTRPCErrorMessage } from '../core/trpc'
import { getDeviceId, getDeviceName } from '../utils/device'
import { removeJwt, storeJwt } from '../utils/jwt'
import { safeRedirectTarget } from '../utils/redirect'
import { SocialSignInCandidate } from '../utils/socialLogin'

const PATH = '/register'

const registerSearchSchema = z.object({
    redirect: z.string().optional(),
})

/**
 * Locally declared rather than reused from `userRegistrationSchema`: that one carries `deviceId` and
 * `deviceName`, which the browser supplies rather than the person, and makes `password` optional
 * because an invited user sets theirs later. Neither is true of this form.
 */
const registerFormSchema = z.object({
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    email: z.string().email(),
    password: z.string().min(8),
})

type RegisterFormSchema = z.infer<typeof registerFormSchema>

const RegisterPage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { redirect } = Route.useSearch()

    const [error, setError] = useState<string | null>(null)
    const [acceptedTerms, setAcceptedTerms] = useState(false)
    const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)
    const [registered, setRegistered] = useState(false)
    const [socialCandidate, setSocialCandidate] = useState<SocialSignInCandidate | null>(null)

    /**
     * Signing up with a provider is finished the moment consent is recorded — unlike the form below,
     * there is no address to confirm, because the provider already vouched for it and the API marks
     * these accounts `isEmailConfirmed`.
     */
    const onSocialSignedIn = useCallback(() => {
        navigate({ to: safeRedirectTarget(redirect) ?? '/' })
    }, [navigate, redirect])

    const onEmailAccountExists = useCallback(
        (email: string, errorKey: SocialEmailAccountExistsError) => {
            navigate({ to: '/login', search: { redirect, email, error: errorKey } })
        },
        [navigate, redirect],
    )

    const form = useForm<RegisterFormSchema>({
        resolver: zodResolver(registerFormSchema),
        defaultValues: { firstName: '', lastName: '', email: '', password: '' },
    })

    const { mutateAsync: register, isPending } = api.user.register.useMutation()
    const { mutateAsync: acceptTerms } = api.user.acceptTerms.useMutation()
    const { mutateAsync: resendConfirmEmail } = api.user.resendConfirmEmail.useMutation()

    const onSubmit = async (data: RegisterFormSchema) => {
        setError(null)

        try {
            const { jwt } = await register({
                ...data,
                deviceId: getDeviceId(),
                deviceName: getDeviceName(),
            })

            /**
             * `acceptTerms` is an authenticated procedure and registration carries no consent
             * fields, so the token has to be stored to record what the person just agreed to. A
             * failure there must not leave a signed-in account with no consent on file, so the
             * token is dropped again and the error surfaced.
             */
            storeJwt(jwt)

            try {
                await acceptTerms({ acceptedTerms: true, acceptedPrivacy: true })
            } catch (caught) {
                removeJwt()
                throw caught
            }

            /**
             * `register` creates the account and nothing else — the confirmation mail is the
             * client's to ask for, which is what the app's ConfirmEmail screen does on mount. This
             * page skipped it and told the user to check an inbox nothing had been sent to.
             *
             * A send that fails does not undo the registration: the account exists, and the login
             * page resends for an unconfirmed address anyway, so the way back is already open.
             */
            await resendConfirmEmail({ email: data.email }).catch(() => undefined)

            // Registration does not confirm the address, and login refuses an unconfirmed one, so
            // the session is dropped here and picked up again after the emailed link is clicked.
            removeJwt()
            setRegistered(true)
        } catch (caught) {
            setError(getTRPCErrorMessage(caught))
        }
    }

    if (socialCandidate) {
        return (
            <SocialConsentStep
                candidate={socialCandidate}
                onSignedIn={onSocialSignedIn}
                onCancel={() => setSocialCandidate(null)}
            />
        )
    }

    if (registered) {
        return (
            <AuthLayout
                title={t('frontend.auth.awaitingEmailConfirmationTitle')}
                subtitle={t('frontend.auth.awaitingEmailConfirmationBody')}
                footer={
                    <Link className="text-primary underline" to="/login" search={{ redirect }}>
                        {t('frontend.auth.backToLogin')}
                    </Link>
                }
            />
        )
    }

    const canSubmit = acceptedTerms && acceptedPrivacy && !isPending

    return (
        <AuthLayout
            title={t('frontend.auth.registerTitle')}
            footer={
                <>
                    {t('frontend.auth.alreadyHaveAccount')}{' '}
                    <Link className="text-primary underline" to="/login" search={{ redirect }}>
                        {t('frontend.auth.login')}
                    </Link>
                </>
            }
        >
            <Form {...form}>
                <FormTextField
                    control={form.control}
                    name="lastName"
                    label={t('frontend.auth.lastName')}
                    inputProps={{ autoComplete: 'family-name', disabled: isPending }}
                />
                <FormTextField
                    control={form.control}
                    name="firstName"
                    label={t('frontend.auth.firstName')}
                    inputProps={{ autoComplete: 'given-name', disabled: isPending }}
                />
                <FormTextField
                    control={form.control}
                    name="email"
                    label={t('frontend.auth.email')}
                    inputProps={{ type: 'email', autoComplete: 'email', disabled: isPending }}
                />
                <FormPasswordField
                    control={form.control}
                    name="password"
                    label={t('frontend.auth.password')}
                    inputProps={{ autoComplete: 'new-password', disabled: isPending }}
                />
            </Form>

            <div className="flex flex-col gap-3">
                <div className="flex items-start gap-3">
                    <Checkbox
                        id="accept-terms"
                        checked={acceptedTerms}
                        onCheckedChange={(value) => setAcceptedTerms(value === true)}
                    />
                    <Label htmlFor="accept-terms">{t('frontend.auth.acceptTerms')}</Label>
                </div>
                <div className="flex items-start gap-3">
                    <Checkbox
                        id="accept-privacy"
                        checked={acceptedPrivacy}
                        onCheckedChange={(value) => setAcceptedPrivacy(value === true)}
                    />
                    <Label htmlFor="accept-privacy">{t('frontend.auth.acceptPrivacy')}</Label>
                </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button
                className={authPillButtonClass}
                disabled={!canSubmit}
                onClick={form.handleSubmit(onSubmit)}
            >
                {isPending && <Icons.Spinner className="mr-2 h-4 animate-spin" />}
                {t('frontend.auth.register')}
            </Button>

            <SocialLoginButtons
                disabled={isPending}
                onSignedIn={onSocialSignedIn}
                onNeedsConsent={setSocialCandidate}
                onError={setError}
                onEmailAccountExists={onEmailAccountExists}
            />
        </AuthLayout>
    )
}

export const Route = createFileRoute(PATH)({
    ssr: false,
    validateSearch: registerSearchSchema,
    head: () => ({ meta: [{ title: 'Long-Y - Regisztráció' }] }),
    component: RegisterPage,
})
